import { ComponentType } from '@angular/cdk/portal';
import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  mergeMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { Reports } from 'src/app/core/enums/role-permissions.enum';
import { UserAdmin } from 'src/app/core/models/admin';
import { AgedRetailStatus } from 'src/app/core/models/reports/reports.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { ErrorReportsService } from 'src/app/core/services/reports/error/error-reports.service';
import { InventoryReportsService } from 'src/app/core/services/reports/inventory/inventory-reports.service';
import { OtherReportsService } from 'src/app/core/services/reports/other/other-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { RolePermissionsUtils } from 'src/app/core/utils/role-permissions/role-permissions.utils';
import { BANNER_TYPE } from '../banner/banner.constants';
import { CollectedComponent } from './report-popups/groups/cods/collected/collected.component';
import { GroupCodExtractComponent } from './report-popups/groups/cods/group-cod-extract/group-cod-extract.component';
import { OutForDeliveryComponent } from './report-popups/groups/cods/out-for-delivery/out-for-delivery.component';
import { OutstandingComponent } from './report-popups/groups/cods/outstanding/outstanding.component';
import { ReconcileReportComponent } from './report-popups/groups/cods/reconcile-report/reconcile-report.component';
import { ReconcileComponent } from './report-popups/groups/cods/reconcile/reconcile.component';
import { GroupErrorReportComponent } from './report-popups/groups/error/group-error-report/group-error-report.component';
import { LocationErrorReportComponent } from './report-popups/groups/error/location-error-report/location-error-report.component';
import { BuildingScanReportComponent } from './report-popups/groups/exceptions-reports/building-scan-report/building-scan-report.component';
import { ExceptionProcessingReportComponent } from './report-popups/groups/exceptions-reports/exception-processing-report/exception-processing-report.component';
import { HandoverScanReportComponent } from './report-popups/groups/exceptions-reports/handover-scan-report/handover-scan-report.component';
import { GroupEdwComponent } from './report-popups/groups/ido/group-edw/group-edw.component';
import { GroupForecastComponent } from './report-popups/groups/ido/group-forecast/group-forecast.component';
import { LocationEdwComponent } from './report-popups/groups/ido/location-edw/location-edw.component';
import { LocationForecastComponent } from './report-popups/groups/ido/location-forecast/location-forecast.component';
import { DeliveryManifestComponent } from './report-popups/groups/on-road-operation/delivery-manifest/delivery-manifest.component';
import { DriverLoadReportComponent } from './report-popups/groups/on-road-operation/driver-load-report/driver-load-report.component';
import { DryIceReportComponent } from './report-popups/groups/on-road-operation/dry-ice-report/dry-ice-report.component';
import { LoadDiscrepenancyReportComponent } from './report-popups/groups/on-road-operation/load-discepenancy-report/load-discepenancy-report.component';
import { PickupManifestComponent } from './report-popups/groups/on-road-operation/pickup-manifest/pickup-manifest.component';
import { SendAgainComponent } from './report-popups/groups/on-road-operation/send-again/send-again.component';
import { AgedPackageStatusComponent } from './report-popups/groups/other/aged-package-status/aged-package-status.component';
import { NonUpsBarcodeExtractComponent } from './report-popups/groups/other/non-ups-barcode-extract/non-ups-barcode-extract.component';
import { RetailActivityReportComponent } from './report-popups/groups/other/retail-activity-report/retail-activity-report.component';
import { GroupSummaryComponent } from './report-popups/groups/summaries/group-summary/group-summary.component';
import { LocationSummaryComponent } from './report-popups/groups/summaries/location-summary/location-summary.component';
import {
  AGED_RETAIL_STATUS_PDF_REPORT,
  CONSTS,
  INVENTORY,
  OTHER,
  REPORT_BUTTON_GROUPS,
  ReportButton,
} from './reports.constants';
@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  standalone: false,
})
export class ReportsComponent implements OnInit, OnDestroy {
  destroy = inject(DestroyRef);
  locationService = inject(LocationService);

  readonly REPORT_BUTTON_GROUPS = REPORT_BUTTON_GROUPS;
  readonly CONSTS = CONSTS;
  readonly AGED_RETAIL_STATUS_PDF_REPORT = AGED_RETAIL_STATUS_PDF_REPORT;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  isIDO!: boolean;
  userRole!: string;
  agedRetailStatusReport!: AgedRetailStatus[];
  locationCode!: string;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly commonUtils: CommonUtils,
    private readonly inventoryReportsService: InventoryReportsService,
    private readonly reportUtils: ReportsUtils,
    private readonly otherReportsService: OtherReportsService,
    private readonly errorReportsService: ErrorReportsService,
    public roleUtils: RolePermissionsUtils,
    private readonly userService: UserService,
    private readonly dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.dataService.setPageHeader(CONSTS.HEADER);

    this.userService
      .getCurrentUser$()
      .pipe(take(1))
      .subscribe((user) => {
        this.userRole = user?.roleName || '';
      });

    // Keep updating isIDO whenever location changes
    this.locationService
      .getCurrentLocation$()
      .pipe(
        map((profile) => !!profile?.location?.routeDeliveryOrder),
        distinctUntilChanged(), // only react when the boolean actually changes
        takeUntil(this.destroy$),
      )
      .subscribe((isIDO) => {
        this.isIDO = isIDO;
      });
  }

  ngOnDestroy(): void {
    this.dataService.setPageHeader('');
    this.destroy$.next();
    this.destroy$.complete();
  }

  buttonGroupClick($event: ReportButton) {
    if ($event.downloadOnly) {
      this.handleDownloadOnlyButtons($event);
    } else {
      this.openModal($event.enum);
    }
  }

  openModal(buttonType: Reports | undefined) {
    switch (buttonType) {
      case Reports.DRIVER_LOAD_REPORT:
        this.openDialog(DriverLoadReportComponent);
        break;
      case Reports.DELIVERY_MANIFEST:
        this.openDialog(DeliveryManifestComponent);
        break;
      case Reports.PICKUP_MANIFEST:
        this.openDialog(PickupManifestComponent);
        break;
      case Reports.LOAD_DISCREPANCY_REPORT:
        this.openDialog(LoadDiscrepenancyReportComponent);
        break;
      case Reports.SEND_AGAIN:
        this.openDialog(SendAgainComponent);
        break;
      case Reports.COD_OUT_FOR_DELIVERY:
        this.openDialog(OutForDeliveryComponent);
        break;
      case Reports.COD_RECONCILE:
        this.openDialog(ReconcileComponent);
        break;
      case Reports.COD_RECONCILE_REPORT:
        this.openDialog(ReconcileReportComponent);
        break;
      case Reports.COD_COLLECTED_CLEARED:
        this.openDialog(CollectedComponent);
        break;
      case Reports.GROUP_COD_EXTRACT:
        this.openDialog(GroupCodExtractComponent);
        break;
      case Reports.COD_OUTSTANDING:
        this.openDialog(OutstandingComponent);
        break;
      case Reports.HANDOVER_SCAN_REPORT:
        this.openDialog(HandoverScanReportComponent);
        break;
      case Reports.EXCEPTION_PROCESSING_REPORT:
        this.openDialog(ExceptionProcessingReportComponent);
        break;
      case Reports.BUILDING_SCAN_EXPORT:
        this.openDialog(BuildingScanReportComponent);
        break;
      case Reports.LOCATION_SUMMARY:
        this.openDialog(LocationSummaryComponent);
        break;
      case Reports.GROUP_SUMMARY:
        this.openDialog(GroupSummaryComponent);
        break;
      case Reports.AGED_PACKAGE_STATUS:
        this.openDialog(AgedPackageStatusComponent);
        break;
      case Reports.RETAIL_ACTIVITY_REPORT:
        this.openDialog(RetailActivityReportComponent);
        break;
      case Reports.NON_UPS_BARCODE_EXTRACT:
        this.openDialog(NonUpsBarcodeExtractComponent);
        break;
      case Reports.LOCATION_FORECAST:
        this.openDialog(LocationForecastComponent);
        break;
      case Reports.GROUP_FORECAST:
        this.openDialog(GroupForecastComponent);
        break;
      case Reports.LOCATION_EDW:
        this.openDialog(LocationEdwComponent);
        break;
      case Reports.GROUP_EDW:
        this.openDialog(GroupEdwComponent);
        break;
      case Reports.DRY_ICE:
        this.openDialog(DryIceReportComponent);
        break;
      case Reports.GROUP_ERROR_REPORT:
        this.openDialog(GroupErrorReportComponent);
        break;
      case Reports.LOCATION_ERROR_REPORT:
        this.openDialog(LocationErrorReportComponent);
        break;
      default:
        console.error('UNIMPLEMENTED');
        break;
    }
  }

  openDialog(
    component: ComponentType<unknown>,
    data?: MatDialogConfig<unknown>,
  ) {
    const screenWidth = window.innerWidth;
    const isPortraitMobile = screenWidth <= 640;
    const isLandscape = screenWidth > 640 && screenWidth <= 932;
    const mobileWidth = 'min(370px, calc(100vw - 1rem))';
    const landscapeWidth = 'min(410px, calc(100vw - 2rem))';
    const mobileMinWidth = 'min(320px, calc(100vw - 1rem))';
    const landscapeMinWidth = 'min(360px, calc(100vw - 2rem))';

    const defaultOption: MatDialogConfig = {
      width: 'auto',
      minWidth: isPortraitMobile
        ? mobileMinWidth
        : isLandscape
          ? landscapeMinWidth
          : '25rem',
      maxWidth: isPortraitMobile
        ? mobileWidth
        : isLandscape
          ? landscapeWidth
          : '95vw',
      disableClose: true,
      autoFocus: false,
    };
    const dataOption = {
      ...defaultOption,
      ...data,
    };
    this.dialog.open(component, !data ? defaultOption : dataOption);
  }

  private handleDownloadOnlyButtons(buttonType: ReportButton) {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          let service!: Observable<void>;
          switch (buttonType.enum) {
            case Reports.LOCATION_INVENTORY:
              service = this.getLocationInventoryReport(user?.location);
              break;
            case Reports.GROUP_INVENTORY:
              service = this.getGroupInventoryReport(user?.locationGroup);
              break;
            case Reports.AGED_RETAIL_STATUS:
              service = this.getAgedRetailStatusReport(user?.location, user);
              break;
            case Reports.PACKAGE_NOT_FOUND:
              service = this.getPackageNotFoundReport(user?.location);
              break;
            default:
              console.error('UNIMPLEMENTED');
              break;
          }
          return service;
        }),
      )
      .subscribe();
  }

  /**
   * Used to get location Inventory Report
   *
   * @private
   * @param {string} locationCode
   * @memberof ReportsComponent
   */
  private getLocationInventoryReport(locationCode: string) {
    return this.inventoryReportsService
      .getLocationInventoryReport(locationCode)
      .pipe(
        take(1),
        map((data) => {
          const locationInventory = data?.response?.locationInventory || [];
          if (locationInventory.length) {
            const customHeader = [
              [
                '',
                '',
                '',
                '',
                '',
                `${INVENTORY.buttons[0].text.toUpperCase()}`,
                '',
                '',
                '',
                '',
                '',
                '',
                '',
              ],
            ];
            this.reportUtils.exportCSVReport(
              locationInventory,
              INVENTORY.buttons[0].text,
              customHeader,
            );
          } else {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
          }
        }),
      );
  }

  /**
   * Used to get Group Inventory Report
   *
   * @private
   * @param {string} groupCode
   * @memberof ReportsComponent
   */
  private getGroupInventoryReport(groupCode: string) {
    return this.inventoryReportsService.getGroupInventoryReport(groupCode).pipe(
      take(1),
      map((data) => {
        const inventory = data?.response?.inventory || [];
        if (inventory.length) {
          const customHeader = [
            [
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              `${INVENTORY.buttons[1].text.toUpperCase()}`,
              '',
              '',
              '',
              '',
              '',
              '',
              '',
            ],
          ];
          this.reportUtils.exportCSVReport(
            inventory,
            INVENTORY.buttons[1].text,
            customHeader,
          );
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
      }),
    );
  }

  /**
   * Used to get Aged Retail Status Report
   *
   * @private
   * @param {string} locationCode
   * @memberof ReportsComponent
   */
  private getAgedRetailStatusReport(locationCode: string, user: UserAdmin) {
    const locale = user.defaultLanguage || 'EN';
    return this.otherReportsService
      .getAgedRetailStatusReport({ locationCode, locale })
      .pipe(
        take(1),
        map((data) => {
          const agedRetailStatus = data?.response?.agedRetailStatus || [];

          if (agedRetailStatus.length) {
            this.locationCode = data.response.location;
            this.agedRetailStatusReport = data.response.agedRetailStatus;
            setTimeout(() => {
              const content = (
                this.pdfContainer.element.nativeElement as HTMLElement
              ).innerHTML;
              this.reportUtils.exportPdfReport(
                content,
                'RETAIL INVENTORY',
                OTHER.buttons[3].text,
              );
            }, 0);
          } else {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
          }
        }),
      );
  }

  /**
   * Used to get packageNotFound Report
   *
   * @private
   * @param {string} locationCode
   * @memberof ReportsComponent
   */
  private getPackageNotFoundReport(locationCode: string) {
    return this.otherReportsService
      .getPackageNotFoundReport({ locationcode: locationCode })
      .pipe(
        take(1),
        map((data) => {
          const packageNotFound = data?.response?.packageNotFound || [];
          if (packageNotFound.length) {
            const customHeader = [
              [
                '',
                '',
                '',
                `${OTHER.buttons[4].text.toUpperCase()}`,
                '',
                '',
                '',
              ],
            ];
            this.reportUtils.exportCSVReport(
              packageNotFound,
              OTHER.buttons[4].text,
              customHeader,
            );
          } else {
            this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
          }
        }),
      );
  }
}
